import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  OnInit,
  PLATFORM_ID,
  TemplateRef,
  WritableSignal,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RoutesRecognized } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzNotificationRef, NzNotificationService } from 'ng-zorro-antd/notification';
import { distinctUntilChanged } from 'rxjs';
import { environment } from '../environments/environment';
import { AppMainComponent } from './components/app-main/app-main.component';
import { AppSideMenuComponent } from './components/app-side-menu/app-side-menu.component';
import { CookieBannerComponent } from './components/cookie-banner/cookie-banner.component';
import { LOCALE_KEY, POS, SupportedLocales, WAYTR } from './constants';
import { stopPropagation } from './helpers';
import {
  AppStateService,
  ApplicationProblemsService,
  CookieConsentService,
  HighlightingService,
  LocalStorageService,
  MemoryMonitorService,
  PerformanceModeService,
  SentryService,
  SessionTimeoutService,
  TitleService,
  UserAccountService,
} from './services';
import { ApiHealthService } from './services/api-health.service';
import { LocalesService } from './services/locales.service';
import {
  WaytrIcon,
  WaytrIconsService,
  waytrAdmin,
  waytrAnalytics,
  waytrArrowDown,
  waytrArrowLeft,
  waytrArrowRight,
  waytrBackspace,
  waytrBarcode,
  waytrBeer,
  waytrBowl,
  waytrBox,
  waytrBreakfast,
  waytrBuilder,
  waytrBurger,
  waytrBusy,
  waytrCake,
  waytrCake2,
  waytrCard,
  waytrCart,
  waytrChange,
  waytrChecklist,
  waytrChevronDown,
  waytrChevronLeft,
  waytrChicken,
  waytrChopsticks,
  waytrClean,
  waytrCutlery,
  waytrDashboard,
  waytrDelete,
  waytrDelivery,
  waytrDineIn,
  waytrDirectSale,
  waytrDiscount,
  waytrDish,
  waytrDrink,
  waytrEdit,
  waytrFaq,
  waytrFeedback,
  waytrFish,
  waytrFolder,
  waytrFoodMenu,
  waytrFresh,
  waytrFries,
  waytrHash,
  waytrHidden,
  waytrId,
  waytrLink,
  waytrLink2,
  waytrList,
  waytrMenu2,
  waytrMove,
  waytrMushrooms,
  waytrNoodle,
  waytrNotification,
  waytrOrders,
  waytrPastry,
  waytrPizza,
  waytrPlus,
  waytrPromoted,
  waytrRecipe,
  waytrReservation,
  waytrRocket,
  waytrSales,
  waytrSaltAndPepper,
  waytrSearch,
  waytrSettings,
  waytrShown,
  waytrSoup,
  waytrSplit,
  waytrStar,
  waytrSupport,
  waytrSushi,
  waytrSweet,
  waytrSwitch,
  waytrTable,
  waytrTableManagement,
  waytrTaco,
  waytrTakeaway,
  waytrTissue,
  waytrUpload,
  waytrWaiter,
  waytrWallet,
  waytrWarning,
  waytrWeight,
  waytrWine,
} from './shared/components';

// Type definition for beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    AppSideMenuComponent,
    AppMainComponent,
    CookieBannerComponent,
    NzIconModule,
    NzButtonModule,
    NgOptimizedImage,
    TranslateModule,
    WaytrIcon,
  ],
})
export class AppComponent implements OnInit {
  readonly template = viewChild<TemplateRef<object>>('installPromptNotification');

  readonly #userAccountService = inject(UserAccountService);
  readonly #titleService = inject(TitleService);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #translateService = inject(TranslateService);
  readonly #notification = inject(NzNotificationService);
  readonly #waytrIconsService = inject(WaytrIconsService);
  readonly #memoryMonitor = inject(MemoryMonitorService);
  readonly #highlightingService = inject(HighlightingService);
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #appStateService = inject(AppStateService);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #cookieConsentService = inject(CookieConsentService);
  readonly #sentryService = inject(SentryService); // Initialize Sentry service early
  readonly #performanceModeService = inject(PerformanceModeService);
  readonly #sessionTimeoutService = inject(SessionTimeoutService); // Monitor session expiration
  readonly #applicationProblemsService = inject(ApplicationProblemsService); // Centralized problem tracking

  protected Breakpoints = Breakpoints;
  protected stopPropagation = stopPropagation;

  private deferredPrompt: WritableSignal<BeforeInstallPromptEvent | null> = signal<BeforeInstallPromptEvent | null>(null);
  protected installPromptVisible: WritableSignal<boolean> = signal<boolean>(false);
  protected installPromptNotification: WritableSignal<NzNotificationRef | null> = signal<NzNotificationRef | null>(null);
  protected sidebarCollapsedClicked: WritableSignal<boolean> = signal<boolean>(false);
  protected hideSidebar: WritableSignal<boolean> = signal<boolean>(false);
  protected mediumBreakpoint: WritableSignal<boolean> = signal<boolean>(false);
  protected smallBreakpoint: WritableSignal<boolean> = signal<boolean>(false);
  protected xsBreakpoint: WritableSignal<boolean> = signal<boolean>(false);
  protected isUserLoggedIn = this.#userAccountService.isUserLoggedIn;

  // Auth background properties
  protected videoPlayer = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');
  protected readonly videoSrc = this.#appStateService.videoSrc;
  protected readonly loginImage = this.#appStateService.loginImage;
  protected readonly videoLoaded = signal(false);
  protected readonly videoError = signal(false);
  // Performance mode - only show video on high-performance devices
  protected readonly enableBackgroundVideo = this.#performanceModeService.enableBackgroundVideo;

  private lastDetailedLog?: number;

  @HostBinding('class.app-not-loaded') get isAppNotLoaded() {
    return !this.isUserLoggedIn();
  }

  @HostBinding('class.hide-sidebar') get isSidebarHidden() {
    return this.hideSidebar();
  }

  @HostBinding('class.sidebar-collapsed') get isSidebarCollapsed() {
    return this.sidebarCollapsedClicked();
  }

  @HostBinding('class.medium') get isMediumBreakpoint() {
    return this.mediumBreakpoint();
  }

  @HostBinding('class.small') get isSmallBreakpoint() {
    return this.smallBreakpoint();
  }

  @HostBinding('class.xs') get isXsBreakpoint() {
    return this.xsBreakpoint();
  }

  readonly #breakpoint$ = this.#breakpointObserver
    .observe([Breakpoints.Medium, Breakpoints.Small, Breakpoints.XSmall])
    .pipe(distinctUntilChanged());

  constructor() {
    // afterNextRender(() => {
    //   const notification = this.#notification.template(this.template!, {
    //     nzDuration: 300000,
    //     nzPlacement: 'bottomRight',
    //   });
    //   this.installPromptNotification.set(notification);
    // });

    if (isPlatformBrowser(this.#platformId)) {
      if ('serviceWorker' in navigator) {
        // window.addEventListener('load', () => {
        //   navigator.serviceWorker.register('waytr-sw.js').catch(error => {
        //     console.error('Service worker registration failed:', error);
        //   });
        // });
        // Handle the `beforeinstallprompt` event
        // window.addEventListener('beforeinstallprompt', (event: Event) => {
        //   this.deferredPrompt.set(event); // Stash the event so it can be triggered later
        // });
      }
    }

    effect(() => {
      const notification = this.installPromptNotification();

      if (!notification) return;

      notification.onClose.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
        this.installPromptVisible.set(false);
      });
    });
    // Initialize memory monitoring
    this.initializeMemoryMonitoring();
  }

  ngOnInit() {
    this.#breakpoint$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.breakpointChanged());
    this.#router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((event) => {
      if (event instanceof RoutesRecognized) {
        const data = event.state.root.firstChild?.data;
        const titleTranslationKey: string = data?.['titleTranslationKey'];
        const hideSidebar: boolean = data?.['hideSidebar'];

        this.#titleService.changeToNewPage(this.#translateService.instant(titleTranslationKey));
        this.hideSidebar.set(hideSidebar);
      }
    });
  }

  protected onVideoLoad() {
    this.videoLoaded.set(true);
    this.#cdr.detectChanges();
  }

  protected onVideoCanPlay() {
    this.videoLoaded.set(true);
    const videoPlayer = this.videoPlayer();
    if (videoPlayer) {
      videoPlayer.nativeElement.muted = true;
      videoPlayer.nativeElement.playbackRate = 0.9;
      videoPlayer.nativeElement.play();
      this.#cdr.detectChanges();
    }
  }

  protected onVideoError() {
    // Video failed to load, fall back to webp image
    this.videoError.set(true);
    this.videoLoaded.set(false);
    this.#cdr.detectChanges();
  }

  protected removeNotification() {
    this.#notification.remove(this.installPromptNotification()?.messageId);
  }

  protected isHighlightActive() {
    return !!this.#highlightingService.highlightTarget();
  }

  protected shouldShowCookieBanner() {
    return this.#cookieConsentService.shouldShowBanner();
  }

  private breakpointChanged() {
    if (this.#breakpointObserver.isMatched(Breakpoints.XSmall)) {
      this.xsBreakpoint.set(true);
      this.smallBreakpoint.set(false);
      this.mediumBreakpoint.set(false);
    } else if (this.#breakpointObserver.isMatched(Breakpoints.Small)) {
      this.smallBreakpoint.set(true);
      this.xsBreakpoint.set(false);
      this.mediumBreakpoint.set(false);
    } else if (this.#breakpointObserver.isMatched(Breakpoints.Medium)) {
      this.mediumBreakpoint.set(true);
      this.xsBreakpoint.set(false);
      this.smallBreakpoint.set(false);
    } else {
      this.xsBreakpoint.set(false);
      this.smallBreakpoint.set(false);
      this.mediumBreakpoint.set(false);
    }
  }

  // Trigger the install prompt
  promptInstall() {
    const deferredPrompt = this.deferredPrompt();

    if (deferredPrompt) {
      deferredPrompt.prompt(); // Show the native install prompt
      deferredPrompt.userChoice.then(() => {
        this.deferredPrompt.set(null); // Clear the prompt event
      });
    }
  }

  // Initialize memory monitoring in development
  private initializeMemoryMonitoring(): void {
    if (!environment.production) {
      const isActive = this.#memoryMonitor.isMonitoringActive();

      // Only log once when memory monitoring is disabled to avoid spam
      if (!isActive) {
        console.info('[App] Memory monitoring disabled for development (Memory API not available)');
        return; // Exit early if monitoring is not active
      }

      console.log('[App] Memory monitoring enabled for development');

      // Log memory stats every 60 seconds in development (reduced frequency)
      setInterval(() => {
        const stats = this.#memoryMonitor.getMemoryStats();

        // Only log if memory API is available (avoid spam when not available)
        if (stats !== 'Memory API not available') {
          console.log(`[Memory] ${stats}`);

          // Log detailed info every 5 minutes for debugging
          const now = Date.now();
          if (!this.lastDetailedLog || now - this.lastDetailedLog > 300000) {
            // 5 minutes
            const detailed = this.#memoryMonitor.getDetailedMemoryStats();
            if (detailed) {
              console.group('[Memory] Detailed Analysis');
              console.log('Compare with Chrome DevTools Memory tab for accuracy');
              console.log('JS Heap (performance.memory):', detailed.jsHeap);
              console.log('Estimated Total Process:', detailed.estimated);
              console.groupEnd();
            }
            this.lastDetailedLog = now;
          }
        }
      }, 60000); // Every 60 seconds
    }
  }

  // Example usage: check if an icon exists
  public iconExists(iconName: string): boolean {
    return this.#waytrIconsService.isWaytrIcon(iconName);
  }
}

export function appInitializerFactory(
  titleService: TitleService,
  localesService: LocalesService,
  localStorageService: LocalStorageService,
  translateService: TranslateService,
  waytrIconsService: WaytrIconsService,
  apiHealthService: ApiHealthService,
  platformId: string | object,
): () => Promise<void> {
  return async () => {
    // Start API health monitoring in the browser
    if (isPlatformBrowser(platformId)) {
      apiHealthService.startMonitoring();
    }

    waytrIconsService.addIcons([
      waytrFresh,
      waytrBowl,
      waytrCart,
      waytrChicken,
      waytrCutlery,
      waytrMushrooms,
      waytrPastry,
      waytrPizza,
      waytrSweet,
      waytrOrders,
      waytrFoodMenu,
      waytrCart,
      waytrTableManagement,
      waytrWaiter,
      waytrDirectSale,
      waytrTable,
      waytrAnalytics,
      waytrNotification,
      waytrBusy,
      waytrStar,
      waytrSales,
      waytrHash,
      waytrLink,
      waytrCard,
      waytrUpload,
      waytrEdit,
      waytrLink2,
      waytrTaco,
      waytrNoodle,
      waytrBreakfast,
      waytrSoup,
      waytrSushi,
      waytrBurger,
      waytrFeedback,
      waytrDrink,
      waytrSaltAndPepper,
      waytrClean,
      waytrTissue,
      waytrArrowLeft,
      waytrArrowRight,
      waytrDiscount,
      waytrSplit,
      waytrChecklist,
      waytrArrowDown,
      waytrSwitch,
      waytrWallet,
      waytrWeight,
      waytrPromoted,
      waytrAdmin,
      waytrSettings,
      waytrChevronLeft,
      waytrChevronDown,
      waytrDashboard,
      waytrChange,
      waytrWarning,
      waytrMenu2,
      waytrBox,
      waytrRecipe,
      waytrList,
      waytrBuilder,
      waytrEdit,
      waytrDelete,
      waytrPlus,
      waytrSearch,
      waytrFolder,
      waytrId,
      waytrBarcode,
      waytrFish,
      waytrMove,
      waytrRocket,
      waytrReservation,
      waytrWine,
      waytrFries,
      waytrChopsticks,
      waytrCake,
      waytrCake2,
      waytrBeer,
      waytrDish,
      waytrShown,
      waytrHidden,
      waytrDineIn,
      waytrTakeaway,
      waytrDelivery,
      waytrBackspace,
      waytrSupport,
      waytrFaq,
    ]);

    if (isPlatformBrowser(platformId)) {
      try {
        const localesPromises = await Promise.all([localStorageService.getData<string>(LOCALE_KEY), localesService.getAllLocales()]);
        const [storedLocale, allLocales] = localesPromises;

        const locale = storedLocale ?? SupportedLocales.RO_RO;

        titleService.loadInitialTitle(`${WAYTR} ${POS}`);
        translateService.setDefaultLang(locale);
        localesService.setLocale(locale as SupportedLocales);
        localesService.setAllLocales(allLocales);
      } catch (error) {
        // Gracefully handle API unavailability during initialization
        console.warn('Failed to load locales from API during initialization. Using defaults.', error);

        // Mark API as unavailable
        apiHealthService.markAsUnavailable('Unable to connect to API server. Please check your connection.');

        const storedLocale = await localStorageService.getData<string>(LOCALE_KEY);
        const locale = storedLocale ?? SupportedLocales.RO_RO;

        titleService.loadInitialTitle(`${WAYTR} ${POS}`);
        translateService.setDefaultLang(locale);
        localesService.setLocale(locale as SupportedLocales);
        // Set empty locales to allow the app to continue
        localesService.setAllLocales({});
      }
    }
  };
}
